// 21 sep 2026 (Augusto, tercera vez: "nada no puedo arrastrar el
// formulario!").
//
// Historia corta de mis dos intentos fallidos, porque explica por qué
// esta versión es tan distinta:
//
// 1. Un PaperComponent con listeners colgados en un useEffect que
//    buscaba el título con querySelector. Dependía de que el efecto
//    corriera con el DOM ya montado.
// 2. El mismo PaperComponent con el handler como prop de React, y
//    aplicado a toda la app desde el tema (MuiDialog.defaultProps).
//    Funcionó SOLO en el diálogo que llevaba la prop escrita a mano; por
//    el tema, no.
//
// Las dos versiones dependían de que MUI montara nuestro componente en
// lugar de su Paper (el prop `as` de emotion). Esta no depende de nada de
// eso: un único listener en `document`, en fase de captura, que actúa
// sobre cualquier diálogo que ya esté en pantalla. No importa quién lo
// haya renderizado, si pasa por el tema, ni si el diálogo existía cuando
// cargó la página.
//
// Se mueve con `left`/`top` y no con `transform`, porque MUI usa
// `transform` para animar la apertura y se pisan. El offset se guarda en
// el propio nodo, así que al cerrar el diálogo (que se desmonta) vuelve
// al centro, que es donde uno espera encontrarlo.

// Margen que siempre queda dentro de la pantalla: sin esto se puede
// arrastrar un diálogo fuera del monitor y quedarte sin forma de traerlo.
const MARGEN_VISIBLE = 56;
// Franja de arriba del diálogo que sirve de asa cuando NO hay barra de
// título (los asistentes de onboarding, por ejemplo).
const ALTO_ASA_SIN_TITULO = 44;

const esControl = (nodo) => !!nodo.closest?.(
  'button, a, input, textarea, select, [role="button"], [role="tab"], .MuiSlider-root'
);

// Devuelve el diálogo a mover si este pointerdown cae en una zona de
// arrastre, o null si no.
const dialogoArrastrable = (e) => {
  const paper = e.target.closest?.('.MuiDialog-paper');
  if (!paper) return null;
  if (esControl(e.target)) return null;

  const titulo = e.target.closest('.MuiDialogTitle-root');
  if (titulo) return paper;

  // Sin barra de título: vale la franja superior, siempre que el clic no
  // caiga dentro del contenido (ahí el usuario está leyendo o
  // seleccionando, no moviendo la ventana).
  if (e.target.closest('.MuiDialogContent-root, .MuiDialogActions-root')) return null;
  const rect = paper.getBoundingClientRect();
  return (e.clientY - rect.top) <= ALTO_ASA_SIN_TITULO ? paper : null;
};

const leerOffset = (paper) => ({
  x: parseFloat(paper.dataset.pcmDragX || '0') || 0,
  y: parseFloat(paper.dataset.pcmDragY || '0') || 0,
});

export default function habilitarArrastreDeDialogos() {
  if (typeof document === 'undefined' || document.__pcmArrastreDialogos) return;
  document.__pcmArrastreDialogos = true;

  // Pista visual: el cursor cambia sobre la zona que se puede agarrar.
  const estilo = document.createElement('style');
  estilo.textContent = `
    .MuiDialog-paper > .MuiDialogTitle-root { cursor: move; user-select: none; touch-action: none; }
  `;
  document.head.appendChild(estilo);

  document.addEventListener('pointerdown', (e) => {
    if (e.button !== 0) return;
    const paper = dialogoArrastrable(e);
    if (!paper) return;

    const base = leerOffset(paper);
    const rect = paper.getBoundingClientRect();
    // Posición "en reposo" (con offset 0), para acotar el arrastre a la
    // pantalla sin importar cuánto se haya movido ya.
    const reposoIzq = rect.left - base.x;
    const reposoArriba = rect.top - base.y;
    const minX = MARGEN_VISIBLE - reposoIzq - rect.width;
    const maxX = window.innerWidth - MARGEN_VISIBLE - reposoIzq;
    const minY = -reposoArriba;
    const maxY = window.innerHeight - MARGEN_VISIBLE - reposoArriba;
    const inicioX = e.clientX;
    const inicioY = e.clientY;

    paper.style.position = 'relative';
    document.body.style.userSelect = 'none';
    e.preventDefault();

    const mover = (ev) => {
      const x = Math.min(Math.max(base.x + (ev.clientX - inicioX), minX), maxX);
      const y = Math.min(Math.max(base.y + (ev.clientY - inicioY), minY), maxY);
      paper.dataset.pcmDragX = String(x);
      paper.dataset.pcmDragY = String(y);
      paper.style.left = `${x}px`;
      paper.style.top = `${y}px`;
    };
    const soltar = () => {
      document.body.style.userSelect = '';
      window.removeEventListener('pointermove', mover);
      window.removeEventListener('pointerup', soltar);
      window.removeEventListener('pointercancel', soltar);
    };
    window.addEventListener('pointermove', mover);
    window.addEventListener('pointerup', soltar);
    window.addEventListener('pointercancel', soltar);
    // Captura: hay que ganarle al onMouseDown que MUI pone en el
    // contenedor del diálogo para detectar clics en el fondo.
  }, true);
}
